import * as React from "react";
import {
  getUserFiles,
  isQueryableUserFileStatus,
  type UserFile,
} from "../../api/finster/userFiles";
import { publishUserFiles } from "../../api/finster/userFilesStore";
import { getSkillOrigin, normalizeSelectedSkills } from "../catalog";
import { refreshSkillsCatalog } from "../catalogStore";
import { getDirectorySkill, getPersonalSkill } from "../client";
import {
  MAX_SKILL_USER_FILE_IDS,
  applySkillUserFileIds,
  createPromptFileSelection,
  getAppliedSkillKey,
  getEffectiveUserFileIds,
  getSkillContributedFileIds,
  getSkillContributorsForFileId,
  reducePromptFileSelection,
  removeSkillFileContribution,
  replaceVisibleUserFileIds,
  retainSkillUserFileIds,
  startPromptFileGeneration,
  type PromptFileSelection,
  type PromptFileSelectionError,
  type PromptFileSelectionResult,
} from "../fileProvenance";
import { toSelectedSkillReference } from "../selection";
import {
  MAX_SELECTED_SKILLS,
  type SelectedSkillReference,
  type Skill,
  type SkillSourceFilterValue,
  type SkillSummary,
  type SkillViewStatus,
} from "../types";
import { useSkillsCatalog } from "./useSkillsCatalog";

export interface UseSkillsControllerOptions {
  enabled: boolean;
  resetKey?: string;
  visibleUserFileIds?: readonly string[];
  browseOpen?: boolean;
}

export type SkillFileHydrationStatus = "loading" | "ready" | "error";

export interface SkillFileHydrationState {
  skill: SelectedSkillReference;
  status: SkillFileHydrationStatus;
  skippedFileCount: number;
  error: Error | PromptFileSelectionError | null;
}

/**
 * The Skill files one submission carried, held between handing the request to
 * the host and hearing whether it was accepted. It records the identity and
 * prompt generation it was taken under so a refusal, an account or chat switch,
 * or a newer prompt cannot have its files written into a conversation that
 * never sent them.
 */
export interface SubmittedSkillFilesSnapshot {
  readonly resetKey: string | undefined;
  readonly promptGeneration: number;
  readonly fileIds: readonly string[];
}

export interface SkillsController {
  enabled: boolean;
  status: SkillViewStatus;
  skills: SkillSummary[];
  catalogError: Error | null;
  selectedSkills: SelectedSkillReference[];
  selectionProvided: boolean;
  selectionAtLimit: boolean;
  pickerOpen: boolean;
  pickerSearch: string;
  pickerSourceFilter: SkillSourceFilterValue;
  browseSearch: string;
  browseSourceFilter: SkillSourceFilterValue;
  browseScrollTop: number;
  detailSummary: SkillSummary | null;
  detail: Skill | null;
  detailStatus: Exclude<SkillViewStatus, "idle">;
  detailError: Error | null;
  promptGeneration: number;
  promptFileSelection: PromptFileSelection;
  effectiveUserFileIds: string[];
  fileDescriptionsByFileId: Readonly<Record<string, string>>;
  fileSelectionError: PromptFileSelectionError | null;
  skillFileHydrations: SkillFileHydrationState[];
  fileHydrationBlocking: boolean;
  setPickerOpen: (open: boolean) => void;
  setPickerSearch: (value: string) => void;
  setPickerSourceFilter: (value: SkillSourceFilterValue) => void;
  setBrowseSearch: (value: string) => void;
  setBrowseSourceFilter: (value: SkillSourceFilterValue) => void;
  setBrowseScrollTop: (scrollTop: number) => void;
  replaceSelectedSkills: (skills: readonly SelectedSkillReference[]) => boolean;
  addSelectedSkill: (skill: SkillSummary | SelectedSkillReference) => boolean;
  removeSelectedSkill: (skill: SelectedSkillReference) => void;
  clearSelectedSkills: () => void;
  captureSubmittedSkillFiles: (
    submittedSkillIds: readonly string[]
  ) => SubmittedSkillFilesSnapshot | null;
  retainAcceptedSkillFiles: (snapshot: SubmittedSkillFilesSnapshot | null) => boolean;
  resetSelectedSkills: () => void;
  replaceVisibleUserFileIds: (fileIds: readonly string[]) => boolean;
  removeUserFileId: (fileId: string) => void;
  retrySkillFileHydration: (skillId: string) => void;
  openDetail: (skill: SkillSummary) => void;
  closeDetail: () => void;
}

function isSkillSummary(skill: SkillSummary | SelectedSkillReference): skill is SkillSummary {
  return "enabled" in skill;
}

function isSkillDetail(skill: SkillSummary | SelectedSkillReference): skill is Skill {
  return "instructions" in skill;
}

function normalizeReferencedFileIds(fileIds: readonly string[]): string[] {
  return Array.from(
    new Set(
      fileIds
        .filter((fileId): fileId is string => typeof fileId === "string")
        .map((fileId) => fileId.trim())
        .filter(Boolean)
    )
  );
}

function createSkillFileLimitError(skillId: string, actual: number): PromptFileSelectionError {
  return {
    code: "skill_file_limit",
    message: `This Skill references ${actual} files, but a Skill can attach at most ${MAX_SKILL_USER_FILE_IDS}. Remove files from the Skill and retry.`,
    limit: MAX_SKILL_USER_FILE_IDS,
    actual,
    skillId,
  };
}

function getHydrationError(): Error {
  return new Error(
    "Attached files could not be prepared. Retry, or remove the Skill before submitting."
  );
}

export function useSkillsController({
  enabled,
  resetKey,
  visibleUserFileIds = [],
  browseOpen = false,
}: UseSkillsControllerOptions): SkillsController {
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;
  const visibleUserFileIdsRef = React.useRef(visibleUserFileIds);
  visibleUserFileIdsRef.current = visibleUserFileIds;

  const [initialFileState] = React.useState(() => {
    const emptySelection = createPromptFileSelection();
    const result = reducePromptFileSelection(emptySelection, {
      type: "replace-manual-files",
      fileIds: visibleUserFileIds,
    });
    return {
      selection: result.ok ? result.selection : emptySelection,
      error: result.ok ? null : result.error,
    };
  });

  const [selectedSkills, setSelectedSkills] = React.useState<SelectedSkillReference[]>([]);
  const selectedSkillsRef = React.useRef(selectedSkills);
  const [selectionProvided, setSelectionProvided] = React.useState(false);
  const [pickerOpen, setPickerOpenState] = React.useState(false);
  const catalog = useSkillsCatalog(enabled, {
    forceRefreshOnFocus: pickerOpen || browseOpen,
  });
  const [pickerSearch, setPickerSearchState] = React.useState("");
  const [pickerSourceFilter, setPickerSourceFilterState] =
    React.useState<SkillSourceFilterValue>("all");
  const [browseSearch, setBrowseSearchState] = React.useState("");
  const [browseSourceFilter, setBrowseSourceFilterState] =
    React.useState<SkillSourceFilterValue>("all");
  const [browseScrollTop, setBrowseScrollTopState] = React.useState(0);
  const [detailSummary, setDetailSummary] = React.useState<SkillSummary | null>(null);
  const [detail, setDetail] = React.useState<Skill | null>(null);
  const [detailStatus, setDetailStatus] =
    React.useState<Exclude<SkillViewStatus, "idle">>("loading");
  const [detailError, setDetailError] = React.useState<Error | null>(null);
  const detailRequestRef = React.useRef<AbortController | null>(null);
  const [promptGeneration, setPromptGeneration] = React.useState(0);
  const promptGenerationRef = React.useRef(promptGeneration);
  const [promptFileSelection, setPromptFileSelection] =
    React.useState<PromptFileSelection>(initialFileState.selection);
  const promptFileSelectionRef = React.useRef(promptFileSelection);
  const [effectiveUserFileIds, setEffectiveUserFileIds] = React.useState<string[]>(() =>
    getEffectiveUserFileIds(initialFileState.selection)
  );
  const [fileSelectionError, setFileSelectionError] =
    React.useState<PromptFileSelectionError | null>(initialFileState.error);
  const [skillFileHydrationsBySkillId, setSkillFileHydrationsBySkillId] = React.useState<
    Record<string, SkillFileHydrationState>
  >({});
  const selectedPersonalSkillsRef = React.useRef(
    new Map<string, { skill: SelectedSkillReference; detail?: Skill }>()
  );
  const hydrationRequestsRef = React.useRef(new Map<string, AbortController>());
  const authenticatedFilesRef = React.useRef<{
    generation: number;
    files: UserFile[];
  } | null>(null);
  const userFilesRequestRef = React.useRef<{
    generation: number;
    controller: AbortController;
    promise: Promise<UserFile[]>;
  } | null>(null);
  const previousEnabledRef = React.useRef(enabled);
  const previousResetKeyRef = React.useRef(resetKey);

  const updateSelectedSkills = (nextSkills: SelectedSkillReference[]) => {
    selectedSkillsRef.current = nextSkills;
    setSelectedSkills(nextSkills);
  };

  const commitFileSelection = (result: PromptFileSelectionResult): boolean => {
    if (!result.ok) {
      setFileSelectionError(result.error);
      return false;
    }
    const skillFileIdsBySkillId: Record<string, string[]> = {};
    for (const skill of selectedSkillsRef.current) {
      const fileIds = result.selection.skillFileIdsBySkillId[skill.id];
      if (fileIds) {
        skillFileIdsBySkillId[skill.id] = fileIds;
      }
    }
    const orderedSelection = {
      ...result.selection,
      skillFileIdsBySkillId,
    };
    promptFileSelectionRef.current = orderedSelection;
    setPromptFileSelection(orderedSelection);
    setEffectiveUserFileIds(getEffectiveUserFileIds(orderedSelection));
    setFileSelectionError(null);
    return true;
  };

  const setSkillFileHydration = (skillId: string, hydration: SkillFileHydrationState | null) => {
    setSkillFileHydrationsBySkillId((current) => {
      if (!hydration) {
        if (!(skillId in current)) return current;
        const next = { ...current };
        delete next[skillId];
        return next;
      }
      return {
        ...current,
        [skillId]: hydration,
      };
    });
  };

  const closeDetail = () => {
    detailRequestRef.current?.abort();
    detailRequestRef.current = null;
    setDetailSummary(null);
    setDetail(null);
    setDetailStatus("loading");
    setDetailError(null);
  };

  React.useEffect(() => {
    detailRequestRef.current?.abort();
    detailRequestRef.current = null;
    hydrationRequestsRef.current.forEach((request) => request.abort());
    hydrationRequestsRef.current.clear();
    userFilesRequestRef.current?.controller.abort();
    userFilesRequestRef.current = null;
    authenticatedFilesRef.current = null;
    selectedPersonalSkillsRef.current.clear();

    selectedSkillsRef.current = [];
    setSelectedSkills([]);
    setSelectionProvided(false);
    setPickerOpenState(false);
    setPickerSearchState("");
    setPickerSourceFilterState("all");
    setBrowseSearchState("");
    setBrowseSourceFilterState("all");
    setBrowseScrollTopState(0);
    setDetailSummary(null);
    setDetail(null);
    setDetailStatus("loading");
    setDetailError(null);
    setSkillFileHydrationsBySkillId({});

    const nextGeneration = promptGenerationRef.current + 1;
    promptGenerationRef.current = nextGeneration;
    setPromptGeneration(nextGeneration);
    const newlyEnabled = enabled && !previousEnabledRef.current;
    const identityChanged = previousResetKeyRef.current !== resetKey;
    previousResetKeyRef.current = resetKey;
    // Starting a generation preserves the manual files, which is right after a
    // submit — the user's own attachments outlive their prompt. It is wrong
    // when the identity behind the selection changed: the files belonged to the
    // account, workbook or chat the user just left, and carrying them forward
    // is how one workbook's confidential attachment ends up scoped to another.
    let nextSelection =
      identityChanged || newlyEnabled
        ? createPromptFileSelection()
        : startPromptFileGeneration(promptFileSelectionRef.current);
    if (identityChanged || newlyEnabled) {
      const visibleFilesResult = reducePromptFileSelection(nextSelection, {
        type: "replace-manual-files",
        fileIds: visibleUserFileIdsRef.current,
      });
      if (visibleFilesResult.ok) {
        nextSelection = visibleFilesResult.selection;
        setFileSelectionError(null);
      } else {
        setFileSelectionError(visibleFilesResult.error);
      }
    } else {
      setFileSelectionError(null);
    }
    promptFileSelectionRef.current = nextSelection;
    setPromptFileSelection(nextSelection);
    setEffectiveUserFileIds(getEffectiveUserFileIds(nextSelection));
    previousEnabledRef.current = enabled;
  }, [enabled, resetKey]);

  React.useEffect(
    () => () => {
      detailRequestRef.current?.abort();
      hydrationRequestsRef.current.forEach((request) => request.abort());
      hydrationRequestsRef.current.clear();
      userFilesRequestRef.current?.controller.abort();
      userFilesRequestRef.current = null;
    },
    []
  );

  React.useEffect(() => {
    if (!enabled || !browseOpen) return;
    void refreshSkillsCatalog({ force: true }).catch(() => undefined);
  }, [browseOpen, enabled]);

  const setPickerOpen = (open: boolean) => {
    const nextOpen = enabledRef.current ? open : false;
    if (nextOpen) {
      void refreshSkillsCatalog({ force: true }).catch(() => undefined);
    }
    setPickerOpenState(nextOpen);
  };

  const setPickerSearch = (value: string) => {
    if (enabledRef.current) setPickerSearchState(value);
  };

  const setPickerSourceFilter = (value: SkillSourceFilterValue) => {
    if (enabledRef.current) setPickerSourceFilterState(value);
  };

  const setBrowseSearch = (value: string) => {
    if (enabledRef.current) setBrowseSearchState(value);
  };

  const setBrowseSourceFilter = (value: SkillSourceFilterValue) => {
    if (enabledRef.current) setBrowseSourceFilterState(value);
  };

  const setBrowseScrollTop = (scrollTop: number) => {
    if (enabledRef.current) setBrowseScrollTopState(scrollTop);
  };

  const loadAuthenticatedUserFiles = (generation: number): Promise<UserFile[]> => {
    if (authenticatedFilesRef.current?.generation === generation) {
      return Promise.resolve(authenticatedFilesRef.current.files);
    }
    if (userFilesRequestRef.current?.generation === generation) {
      return userFilesRequestRef.current.promise;
    }

    const controller = new AbortController();
    let promise!: Promise<UserFile[]>;
    promise = getUserFiles(controller.signal)
      .then((files) => {
        if (
          controller.signal.aborted ||
          !enabledRef.current ||
          promptGenerationRef.current !== generation
        ) {
          return files;
        }
        authenticatedFilesRef.current = { generation, files };
        publishUserFiles(files);
        return files;
      })
      .finally(() => {
        if (userFilesRequestRef.current?.promise === promise) {
          userFilesRequestRef.current = null;
        }
      });
    userFilesRequestRef.current = { generation, controller, promise };
    return promise;
  };

  const startSkillFileHydration = (
    selection: { skill: SelectedSkillReference; detail?: Skill },
    options: { refreshUserFiles?: boolean } = {}
  ) => {
    const { skill } = selection;
    if (!enabledRef.current || !selectedPersonalSkillsRef.current.has(skill.id)) return;

    const generation = promptGenerationRef.current;
    const appliedKey = getAppliedSkillKey(generation, skill.id);
    if (promptFileSelectionRef.current.appliedSkillKeys.includes(appliedKey)) return;

    hydrationRequestsRef.current.get(skill.id)?.abort();
    if (options.refreshUserFiles && !userFilesRequestRef.current) {
      authenticatedFilesRef.current = null;
    }
    const request = new AbortController();
    hydrationRequestsRef.current.set(skill.id, request);
    setSkillFileHydration(skill.id, {
      skill,
      status: "loading",
      skippedFileCount: 0,
      error: null,
    });

    const isCurrentRequest = () =>
      !request.signal.aborted &&
      enabledRef.current &&
      promptGenerationRef.current === generation &&
      hydrationRequestsRef.current.get(skill.id) === request &&
      selectedPersonalSkillsRef.current.has(skill.id);

    void (async () => {
      try {
        const loadedSkill = selection.detail ?? (await getPersonalSkill(skill.id, request.signal));
        if (!isCurrentRequest()) return;
        if (loadedSkill.id !== skill.id || getSkillOrigin(loadedSkill) !== "personal") {
          throw new Error("The selected Skill detail did not match the request.");
        }

        const referencedFileIds = normalizeReferencedFileIds(loadedSkill.userFileIds ?? []);
        if (referencedFileIds.length > MAX_SKILL_USER_FILE_IDS) {
          setSkillFileHydration(skill.id, {
            skill,
            status: "error",
            skippedFileCount: 0,
            error: createSkillFileLimitError(skill.id, referencedFileIds.length),
          });
          return;
        }

        let readyFileIds: string[] = [];
        let skippedFileCount = 0;
        if (referencedFileIds.length > 0) {
          const userFiles = await loadAuthenticatedUserFiles(generation);
          if (!isCurrentRequest()) return;
          const userFilesById = new Map(userFiles.map((file) => [file.id, file]));
          readyFileIds = referencedFileIds.filter((fileId) => {
            const file = userFilesById.get(fileId);
            return Boolean(file && isQueryableUserFileStatus(file.status));
          });
          skippedFileCount = referencedFileIds.length - readyFileIds.length;
        }

        const result = applySkillUserFileIds(
          promptFileSelectionRef.current,
          generation,
          skill.id,
          readyFileIds
        );
        if (!isCurrentRequest()) return;
        if (!result.ok) {
          setSkillFileHydration(skill.id, {
            skill,
            status: "error",
            skippedFileCount,
            error: result.error,
          });
          return;
        }

        commitFileSelection(result);
        setSkillFileHydration(skill.id, {
          skill,
          status: "ready",
          skippedFileCount,
          error: null,
        });
      } catch {
        if (!isCurrentRequest()) return;
        setSkillFileHydration(skill.id, {
          skill,
          status: "error",
          skippedFileCount: 0,
          error: getHydrationError(),
        });
      } finally {
        if (hydrationRequestsRef.current.get(skill.id) === request) {
          hydrationRequestsRef.current.delete(skill.id);
        }
      }
    })();
  };

  const addSelectedSkill = (skill: SkillSummary | SelectedSkillReference): boolean => {
    if (!enabledRef.current || (isSkillSummary(skill) && !skill.enabled)) {
      return false;
    }

    const reference = isSkillSummary(skill) ? toSelectedSkillReference(skill) : skill;
    const current = selectedSkillsRef.current;
    const next = normalizeSelectedSkills([...current, reference]);
    if (next.length === current.length) return false;

    updateSelectedSkills(next);
    setSelectionProvided(true);
    if (isSkillSummary(skill) && getSkillOrigin(skill) === "personal") {
      const personalSelection = {
        skill: reference,
        detail: isSkillDetail(skill) ? skill : undefined,
      };
      selectedPersonalSkillsRef.current.set(reference.id, personalSelection);
      startSkillFileHydration(personalSelection);
    }
    return true;
  };

  const replaceSelectedSkills = (skills: readonly SelectedSkillReference[]): boolean => {
    if (!enabledRef.current) return false;

    const next = normalizeSelectedSkills(skills);
    const current = selectedSkillsRef.current;
    const unchanged =
      next.length === current.length &&
      next.every(
        (skill, index) =>
          skill.id === current[index]?.id && skill.slug === current[index]?.slug
      );
    if (unchanged) return false;
    // Deriving this from `next.length` made an emptied picker indistinguishable
    // from one the user never touched, so a clarification kept inheriting the
    // frozen Skill the user had just removed. Any real change to the selection
    // — including removing the last Skill — is the user choosing the scope of
    // the next request; only a submit or an identity reset takes it back.
    setSelectionProvided(true);

    const nextIds = new Set(next.map((skill) => skill.id));
    const removed = current.filter((skill) => !nextIds.has(skill.id));
    const currentIds = new Set(current.map((skill) => skill.id));
    const added = next.filter((skill) => !currentIds.has(skill.id));

    updateSelectedSkills(next);

    let nextFileSelection = promptFileSelectionRef.current;
    for (const skill of removed) {
      hydrationRequestsRef.current.get(skill.id)?.abort();
      hydrationRequestsRef.current.delete(skill.id);
      selectedPersonalSkillsRef.current.delete(skill.id);
      setSkillFileHydration(skill.id, null);
      nextFileSelection = removeSkillFileContribution(
        nextFileSelection,
        promptGenerationRef.current,
        skill.id
      );
    }
    commitFileSelection({ ok: true, selection: nextFileSelection });

    for (const reference of added) {
      const summary = catalog.skills.find((skill) => skill.id === reference.id);
      if (!summary || getSkillOrigin(summary) !== "personal") continue;
      const personalSelection = { skill: reference };
      selectedPersonalSkillsRef.current.set(reference.id, personalSelection);
      startSkillFileHydration(personalSelection);
    }

    if (selectedPersonalSkillsRef.current.size === 0) {
      userFilesRequestRef.current?.controller.abort();
      userFilesRequestRef.current = null;
    }
    return true;
  };

  const removeSelectedSkill = (skill: SelectedSkillReference) => {
    if (!enabledRef.current) return;
    const removedSkills = selectedSkillsRef.current.filter(
      (selected) => selected.id === skill.id || selected.slug === skill.slug
    );
    updateSelectedSkills(
      selectedSkillsRef.current.filter(
        (selected) => selected.id !== skill.id && selected.slug !== skill.slug
      )
    );
    let nextFileSelection = promptFileSelectionRef.current;
    for (const removedSkill of removedSkills) {
      hydrationRequestsRef.current.get(removedSkill.id)?.abort();
      hydrationRequestsRef.current.delete(removedSkill.id);
      selectedPersonalSkillsRef.current.delete(removedSkill.id);
      setSkillFileHydration(removedSkill.id, null);
      nextFileSelection = removeSkillFileContribution(
        nextFileSelection,
        promptGenerationRef.current,
        removedSkill.id
      );
    }
    commitFileSelection({ ok: true, selection: nextFileSelection });
    if (selectedPersonalSkillsRef.current.size === 0) {
      userFilesRequestRef.current?.controller.abort();
      userFilesRequestRef.current = null;
    }
    setSelectionProvided(true);
  };

  const clearSelectedSkills = () => {
    if (!enabledRef.current) return;
    hydrationRequestsRef.current.forEach((request) => request.abort());
    hydrationRequestsRef.current.clear();
    userFilesRequestRef.current?.controller.abort();
    userFilesRequestRef.current = null;
    selectedPersonalSkillsRef.current.clear();
    let nextFileSelection = promptFileSelectionRef.current;
    for (const skill of selectedSkillsRef.current) {
      nextFileSelection = removeSkillFileContribution(
        nextFileSelection,
        promptGenerationRef.current,
        skill.id
      );
    }
    updateSelectedSkills([]);
    setSkillFileHydrationsBySkillId({});
    commitFileSelection({ ok: true, selection: nextFileSelection });
    setSelectionProvided(true);
  };

  const captureSubmittedSkillFiles = (
    submittedSkillIds: readonly string[]
  ): SubmittedSkillFilesSnapshot | null => {
    if (!enabledRef.current) return null;
    // Only the Skills the submitted prompt actually named contribute. A Skill
    // the user removed before sending, or one selected for the next prompt
    // while this one was in flight, has nothing to do with this request.
    const fileIds = getSkillContributedFileIds(promptFileSelectionRef.current, submittedSkillIds);
    if (fileIds.length === 0) return null;
    return {
      resetKey: previousResetKeyRef.current,
      promptGeneration: promptGenerationRef.current,
      fileIds,
    };
  };

  const retainAcceptedSkillFiles = (snapshot: SubmittedSkillFilesSnapshot | null): boolean => {
    if (!enabledRef.current || !snapshot || snapshot.fileIds.length === 0) return false;
    // A host can take its time deciding. By the time it answers, the user may
    // have switched account, deck, workbook or chat, or already sent something
    // else — in which case these files belong to a conversation that is no
    // longer the one on screen and must not be written into this one.
    if (
      snapshot.resetKey !== previousResetKeyRef.current ||
      snapshot.promptGeneration !== promptGenerationRef.current
    ) {
      return false;
    }
    return commitFileSelection(
      retainSkillUserFileIds(promptFileSelectionRef.current, snapshot.fileIds)
    );
  };

  const resetSelectedSkills = () => {
    hydrationRequestsRef.current.forEach((request) => request.abort());
    hydrationRequestsRef.current.clear();
    userFilesRequestRef.current?.controller.abort();
    userFilesRequestRef.current = null;
    authenticatedFilesRef.current = null;
    selectedPersonalSkillsRef.current.clear();
    updateSelectedSkills([]);
    setSkillFileHydrationsBySkillId({});
    const nextGeneration = promptGenerationRef.current + 1;
    promptGenerationRef.current = nextGeneration;
    setPromptGeneration(nextGeneration);
    commitFileSelection({
      ok: true,
      selection: startPromptFileGeneration(promptFileSelectionRef.current),
    });
    setSelectionProvided(false);
  };

  const replaceVisibleFiles = (fileIds: readonly string[]): boolean => {
    if (!enabledRef.current) return false;
    return commitFileSelection(replaceVisibleUserFileIds(promptFileSelectionRef.current, fileIds));
  };

  const removeUserFileId = (fileId: string) => {
    if (!enabledRef.current) return;
    commitFileSelection(
      reducePromptFileSelection(promptFileSelectionRef.current, {
        type: "remove-file",
        fileId,
      })
    );
  };

  const retrySkillFileHydration = (skillId: string) => {
    if (!enabledRef.current) return;
    const personalSelection = selectedPersonalSkillsRef.current.get(skillId);
    if (!personalSelection) return;
    startSkillFileHydration(personalSelection, { refreshUserFiles: true });
  };

  const openDetail = (skill: SkillSummary) => {
    if (!enabledRef.current || !skill.enabled) return;

    detailRequestRef.current?.abort();
    const request = new AbortController();
    detailRequestRef.current = request;
    setDetailSummary(skill);
    setDetail(null);
    setDetailStatus("loading");
    setDetailError(null);

    const loadSkill = getSkillOrigin(skill) === "personal" ? getPersonalSkill : getDirectorySkill;
    void loadSkill(skill.id, request.signal)
      .then((loadedSkill) => {
        if (request.signal.aborted || detailRequestRef.current !== request || !enabledRef.current) {
          return;
        }
        setDetail(loadedSkill);
        setDetailStatus("ready");
      })
      .catch((error: unknown) => {
        if (request.signal.aborted || detailRequestRef.current !== request || !enabledRef.current) {
          return;
        }
        setDetailError(error instanceof Error ? error : new Error("Unable to load this Skill."));
        setDetailStatus("error");
      });
  };

  const skillFileHydrations = selectedSkills
    .map((skill) => skillFileHydrationsBySkillId[skill.id])
    .filter((hydration): hydration is SkillFileHydrationState => hydration !== undefined);
  const fileHydrationBlocking = skillFileHydrations.some(
    (hydration) => hydration.status === "loading" || hydration.status === "error"
  );
  const fileDescriptionsByFileId: Record<string, string> = {};
  for (const fileId of effectiveUserFileIds) {
    const contributorIds = new Set(getSkillContributorsForFileId(promptFileSelection, fileId));
    const slugs = selectedSkills
      .filter((skill) => contributorIds.has(skill.id))
      .map((skill) => `/${skill.slug}`);
    if (slugs.length > 0) {
      fileDescriptionsByFileId[fileId] = `From ${slugs.join(", ")}`;
    }
  }

  return {
    enabled,
    status: catalog.status,
    skills: catalog.skills,
    catalogError: catalog.error,
    selectedSkills,
    selectionProvided,
    selectionAtLimit: selectedSkills.length >= MAX_SELECTED_SKILLS,
    pickerOpen,
    pickerSearch,
    pickerSourceFilter,
    browseSearch,
    browseSourceFilter,
    browseScrollTop,
    detailSummary,
    detail,
    detailStatus,
    detailError,
    promptGeneration,
    promptFileSelection,
    effectiveUserFileIds,
    fileDescriptionsByFileId,
    fileSelectionError,
    skillFileHydrations,
    fileHydrationBlocking,
    setPickerOpen,
    setPickerSearch,
    setPickerSourceFilter,
    setBrowseSearch,
    setBrowseSourceFilter,
    setBrowseScrollTop,
    replaceSelectedSkills,
    addSelectedSkill,
    removeSelectedSkill,
    clearSelectedSkills,
    captureSubmittedSkillFiles,
    retainAcceptedSkillFiles,
    resetSelectedSkills,
    replaceVisibleUserFileIds: replaceVisibleFiles,
    removeUserFileId,
    retrySkillFileHydration,
    openDetail,
    closeDetail,
  };
}
